import React, { useEffect, useState } from "react";
import * as actions from "../Common/actions";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, Box, Paper, TextField, Typography, Divider, Chip, InputAdornment, Switch } from "@mui/material";
import CustomAddModal from "../Common/CustomAddModal";
import CustomShowModal from "../Common/customShowModal";
import { toastSuccess, toastError, checkUserDetailsSession, setUserDetailsSession, clearUserDetailsSession } from "../../utils";
import moment from "moment";
import UserDetailsPasswordModal from './UserDetailsPasswordModal';
import CopyableField from '../../components/CopyableField';
import ActionButton from '../../components/ActionButton';
import StatCard from '../../components/StatCard';
import { User, Mail, Phone, MapPin, Calendar, Shield, DollarSign, Disc3, FileText, Users, Ticket, Landmark, Clock, Activity, StickyNote, List, ListOrdered, Pencil, Save, X } from 'lucide-react';

// Helper function to calculate time ago with detailed breakdown
const getTimeAgo = (dateString) => {
  if (!dateString) return "-";

  const updatedDate = new Date(dateString);
  const now = new Date();

  let years = now.getFullYear() - updatedDate.getFullYear();
  let months = now.getMonth() - updatedDate.getMonth();
  let days = now.getDate() - updatedDate.getDate();

  if (days < 0) {
    months--;
    const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += lastMonth.getDate();
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
  if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

  if (parts.length === 0) return "Today";
  return parts.join(', ');
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function ViewUserDetails(props) {
  const params = useParams();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState({});
  const [bankDetails, setBankDetails] = useState({});
  const [openRevenue, setOpenRevenue] = useState(false);
  const [openRevenueLogs, setOpenRevenueLogs] = useState(false);
  const [addRevenue, setAddRevenue] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [editUserData, setEditUserData] = useState({ name: "", phone: "", address: "", analytics: "false", internal_notes: "" });
  const [notesEditMode, setNotesEditMode] = useState(false);
  const [notesEditValue, setNotesEditValue] = useState("");

  useEffect(() => {
    const sessionValid = checkUserDetailsSession();
    if (sessionValid) {
      setIsAuthenticated(true);
      getUserDetailsById();
    } else {
      setShowPasswordModal(true);
      clearUserDetailsSession();
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const intervalId = setInterval(() => {
      const sessionValid = checkUserDetailsSession();
      if (!sessionValid) {
        setIsAuthenticated(false);
        setShowPasswordModal(true);
        setPasswordError('Your session has expired. Please re-enter the password.');
      }
    }, 300000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        const sessionValid = checkUserDetailsSession();
        if (!sessionValid) {
          setIsAuthenticated(false);
          setShowPasswordModal(true);
          setPasswordError('Your session has expired. Please re-enter the password.');
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  const getUserDetailsById = () => {
    props.setLoader(true);
    actions
      .getUserDetailsByIdForAdmin(params.id)
      .then((res) => {
        props.setLoader(false);
        setUserDetails(res.userDetails);
        setBankDetails(res.bankDetails);
        setAddRevenue(res.userDetails.revenue);
        setEditUserData({ name: res.userDetails.name || "", phone: res.userDetails.phone || "", address: res.userDetails.address || "", analytics: res.userDetails.analytics || "false", internal_notes: res.userDetails.internal_notes || "" });
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load user details');
        navigate("/user_details");
      });
  };

  const handlePasswordVerification = (password) => {
    setPasswordLoading(true);
    setPasswordError('');
    actions.verifyUserDetailsPassword({ password })
      .then((res) => {
        setPasswordLoading(false);
        setShowPasswordModal(false);
        setIsAuthenticated(true);
        setUserDetailsSession(Date.now());
        getUserDetailsById();
        toastSuccess('Access granted');
      })
      .catch((err) => {
        setPasswordLoading(false);
        setPasswordError(err?.message || 'Invalid password. Please try again.');
      });
  };

  const onUpdateRevenue = () => {
    props.setLoader(true);
    actions
      .addRevenueAdmin({ user_id: params.id, revenue: addRevenue })
      .then((res) => {
        setOpenRevenue(false);
        getUserDetailsById();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to add revenue');
      });
  };

  const onUpdateUserDetails = () => {
    props.setLoader(true);
    actions
      .updateUserDetailsAdmin({ user_id: params.id, name: editUserData.name, phone: editUserData.phone, address: editUserData.address, analytics: editUserData.analytics, internal_notes: (editUserData.internal_notes || "").trim() })
      .then((res) => {
        setEditUserModal(false);
        getUserDetailsById();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to update user details');
      });
  };

  const {
    name = "", email = "", phone = "", address = "", created_at = "",
    ip = "", revenue = "", revoked = "", total_releases = "",
    total_drafts = "", total_approved = 0, total_rejected = 0,
    total_takendown = 0, total_in_review = 0,
    total_artist = "", total_reports_requested = "",
    total_tickets_created = "", total_open_tickets = "",
    internal_notes = "",
  } = userDetails;

  const {
    bank_name = "", ifsc_code = "", account_number = "", holder_name = "",
    branch_name = "", pan = "", other_details = "", updated_on = "",
  } = bankDetails;

  const notesTextareaRef = React.useRef(null);

  const handleNotesEditStart = () => {
    setNotesEditValue(internal_notes || "");
    setNotesEditMode(true);
  };

  const handleNotesEditCancel = () => {
    setNotesEditMode(false);
    setNotesEditValue("");
  };

  const handleNotesSave = () => {
    const trimmed = notesEditValue.trim();
    props.setLoader(true);
    actions
      .updateUserDetailsAdmin({
        user_id: params.id,
        name: editUserData.name,
        phone: editUserData.phone,
        address: editUserData.address,
        analytics: editUserData.analytics,
        internal_notes: trimmed,
      })
      .then((res) => {
        setNotesEditMode(false);
        setNotesEditValue("");
        getUserDetailsById();
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || "Failed to save notes");
      });
  };

  const handleNotesKeyDown = (e) => {
    const textarea = notesTextareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd } = textarea;
    // Only act when cursor is a caret (no selection)
    if (selectionStart !== selectionEnd) return;

    const value = notesEditValue;
    const beforeCursor = value.slice(0, selectionStart);
    const lastNewline = beforeCursor.lastIndexOf("\n");
    const currentLine = beforeCursor.slice(lastNewline + 1);

    if (e.key === "Enter") {
      // Check if the current line is a bullet line
      if (currentLine === "\u2022 ") {
        // Empty bullet — remove it
        e.preventDefault();
        const newValue = value.slice(0, selectionStart - 2) + value.slice(selectionStart);
        setNotesEditValue(newValue);
        setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = selectionStart - 2; }, 0);
        return;
      }
      if (currentLine.startsWith("\u2022 ")) {
        // Continue bullet on next line
        e.preventDefault();
        const insert = "\n\u2022 ";
        const newValue = value.slice(0, selectionStart) + insert + value.slice(selectionStart);
        setNotesEditValue(newValue);
        setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = selectionStart + insert.length; }, 0);
        return;
      }

      // Check if the current line is a numbered line
      const numMatch = currentLine.match(/^(\d+)\.\s$/);
      if (numMatch) {
        // Empty numbered line — remove it
        e.preventDefault();
        const prefixLen = numMatch[0].length;
        const newValue = value.slice(0, selectionStart - prefixLen) + value.slice(selectionStart);
        setNotesEditValue(newValue);
        setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = selectionStart - prefixLen; }, 0);
        return;
      }
      const numContentMatch = currentLine.match(/^(\d+)\.\s/);
      if (numContentMatch) {
        // Continue numbered list with next number
        e.preventDefault();
        const nextNum = parseInt(numContentMatch[1]) + 1;
        const insert = "\n" + nextNum + ". ";
        const newValue = value.slice(0, selectionStart) + insert + value.slice(selectionStart);
        setNotesEditValue(newValue);
        setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = selectionStart + insert.length; }, 0);
        return;
      }
    }
  };

  const toggleBulletList = () => {
    const textarea = notesTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const lines = notesEditValue.split("\n");
    let charCount = 0;
    let startLine = 0, endLine = 0;
    for (let i = 0; i < lines.length; i++) {
      const lineEnd = charCount + lines[i].length;
      if (charCount <= start && start <= lineEnd) startLine = i;
      if (charCount <= end && end <= lineEnd) endLine = i;
      charCount += lines[i].length + 1;
    }
    const newLines = lines.map((line, i) => {
      if (i < startLine || i > endLine) return line;
      if (line.startsWith("\u2022 ")) return line.slice(2);
      const numberedMatch = line.match(/^\d+\.\s/);
      if (numberedMatch) return "\u2022 " + line.slice(numberedMatch[0].length);
      return "\u2022 " + line;
    });
    setNotesEditValue(newLines.join("\n"));
  };

  const toggleNumberedList = () => {
    const textarea = notesTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const lines = notesEditValue.split("\n");
    let charCount = 0;
    let startLine = 0, endLine = 0;
    for (let i = 0; i < lines.length; i++) {
      const lineEnd = charCount + lines[i].length;
      if (charCount <= start && start <= lineEnd) startLine = i;
      if (charCount <= end && end <= lineEnd) endLine = i;
      charCount += lines[i].length + 1;
    }
    let num = 1;
    const newLines = lines.map((line, i) => {
      if (i < startLine || i > endLine) return line;
      const numberedMatch = line.match(/^\d+\.\s/);
      if (numberedMatch) return line.slice(numberedMatch[0].length);
      if (line.startsWith("\u2022 ")) return num++ + ". " + line.slice(2);
      return num++ + ". " + line;
    });
    setNotesEditValue(newLines.join("\n"));
  };

  const renderNotesContent = (text) => {
    if (!text) {
      return (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontStyle: 'italic', py: 2, textAlign: 'center' }}>
          No internal notes yet. Click Edit to add notes.
        </Typography>
      );
    }
    const lines = text.split("\n");
    const elements = [];
    let prevEmpty = false;
    lines.forEach((line, i) => {
      if (line.trim() === "") {
        if (!prevEmpty) elements.push(<Box key={`sp-${i}`} sx={{ height: 8 }} />);
        prevEmpty = true;
        return;
      }
      prevEmpty = false;
      if (line.startsWith("\u2022 ")) {
        elements.push(
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.3, pl: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#6C63FF', mt: '7px', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
              {line.slice(2)}
            </Typography>
          </Box>
        );
      } else if (/^\d+\.\s/.test(line)) {
        const match = line.match(/^(\d+)\.\s(.*)/);
        elements.push(
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.3, pl: 1 }}>
            <Typography variant="body2" sx={{ color: '#6C63FF', fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.6, minWidth: 18 }}>
              {match[1]}.
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6 }}>
              {match[2]}
            </Typography>
          </Box>
        );
      } else {
        elements.push(
          <Typography key={i} variant="body2" sx={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.6, py: 0.3, textAlign: 'left' }}>
            {line}
          </Typography>
        );
      }
    });
    return elements;
  };

  useEffect(() => {
    if (name) {
      document.title = `UD | ${name}`;
    }
    return () => { document.title = 'Fronic Media Admin'; };
  }, [name]);

  if (!isAuthenticated) {
    return (
      <UserDetailsPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />
    );
  }

  return (
    <>
      <UserDetailsPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />

      <Grid container className="view-release-container" spacing={2}>
        {/* ==================== HEADER ==================== */}
        <Grid item xs={12}>
          <Box className="view-release-header">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                {name || 'User Details'}
              </Typography>
              {email && (
                <Chip
                  size="small"
                  label={email}
                  icon={<Mail size={13} />}
                  sx={{
                    backgroundColor: 'rgba(108, 99, 255, 0.15)',
                    color: '#6C63FF',
                    fontWeight: 500,
                    fontSize: '0.78rem',
                    flexShrink: 0,
                    '& .MuiChip-icon': { color: '#6C63FF' },
                  }}
                />
              )}
              {revoked === 1 && (
                <Chip
                  size="small"
                  label="Revoked"
                  sx={{
                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                    color: '#EF4444',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                  }}
                />
              )}
            </Box>
            <ActionButton variant="settle" onClick={() => navigate('/user_details')}>
              Back to Users
            </ActionButton>
          </Box>
        </Grid>

        {/* ==================== LEFT SIDEBAR ==================== */}
        <Grid item xs={12} md={3}>
          <Box className="view-release-sidebar">
            {/* User Avatar Area */}
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '24px 16px', backgroundColor: 'var(--bg-elevated)',
              borderRadius: '8px', border: '1px solid var(--border)',
            }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                backgroundColor: 'rgba(108, 99, 255, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
              }}>
                <User size={32} color="#6C63FF" />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center' }}>
                {name || '-'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'center' }}>
                Joined {moment(created_at).format("DD MMM YYYY")}
              </Typography>
            </Box>

            {/* Quick Contact Info */}
            <Box mt={2} className="sidebar-user-info">
              <CopyableField label="Email" value={email} />
              <CopyableField label="Phone" value={phone} />
              <CopyableField label="IP" value={ip} />
            </Box>

            {/* Actions */}
            <Box mt={2} className="sidebar-actions">
              <Typography variant="subtitle2" className="sidebar-actions__title">Actions</Typography>
              <div className="sidebar-actions__buttons">
                <ActionButton variant="success" onClick={() => setOpenRevenue(true)}>
                  Add Revenue
                </ActionButton>
                <ActionButton variant="info" onClick={() => setOpenRevenueLogs(true)}>
                  Revenue Logs
                </ActionButton>
              </div>
            </Box>

            {/* Internal Notes */}
            <Box mt={2} sx={{
              backgroundColor: 'var(--bg-elevated)',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              p: 2,
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={0.75}>
                  <StickyNote size={15} color="#6C63FF" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                    Internal Notes
                  </Typography>
                </Box>
                {!notesEditMode && (
                  <Box
                    component="button"
                    onClick={handleNotesEditStart}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.4,
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#6C63FF', fontSize: '0.75rem', fontWeight: 500,
                      p: 0, '&:hover': { opacity: 0.8 },
                    }}
                  >
                    <Pencil size={12} />
                    Edit
                  </Box>
                )}
              </Box>
              <Divider sx={{ mb: 1.5, borderColor: 'var(--border)' }} />

              {notesEditMode ? (
                <Box>
                  {/* Formatting Toolbar */}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                    <Box
                      component="button"
                      onClick={toggleBulletList}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, border: '1px solid var(--border)',
                        borderRadius: '6px', backgroundColor: 'rgba(108, 99, 255, 0.15)',
                        cursor: 'pointer', color: '#6C63FF', transition: 'all 0.2s',
                        '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.25)' },
                      }}
                      title="Bullet list"
                    >
                      <List size={14} />
                    </Box>
                    <Box
                      component="button"
                      onClick={toggleNumberedList}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, border: '1px solid var(--border)',
                        borderRadius: '6px', backgroundColor: 'rgba(108, 99, 255, 0.15)',
                        cursor: 'pointer', color: '#6C63FF', transition: 'all 0.2s',
                        '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.25)' },
                      }}
                      title="Numbered list"
                    >
                      <ListOrdered size={14} />
                    </Box>
                  </Box>

                  <TextField
                    inputRef={notesTextareaRef}
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={12}
                    value={notesEditValue}
                    onChange={(e) => setNotesEditValue(e.target.value)}
                    onKeyDown={handleNotesKeyDown}
                    placeholder="Add internal notes..."
                    variant="outlined"
                    size="small"
                    sx={{
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8rem',
                        fontFamily: 'inherit',
                        '& fieldset': { borderColor: 'var(--border)' },
                        '&:hover fieldset': { borderColor: '#6C63FF' },
                        '&.Mui-focused fieldset': { borderColor: '#6C63FF' },
                      },
                      '& .MuiInputBase-input': {
                        color: 'var(--text-primary)',
                      },
                    }}
                  />

                  <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                    <ActionButton variant="info" onClick={handleNotesEditCancel}>
                      <Box display="flex" alignItems="center" gap={0.4} sx={{ fontSize: '0.75rem' }}>
                        <X size={12} />
                        Cancel
                      </Box>
                    </ActionButton>
                    <ActionButton variant="success" onClick={handleNotesSave}>
                      <Box display="flex" alignItems="center" gap={0.4} sx={{ fontSize: '0.75rem' }}>
                        <Save size={12} />
                        Save
                      </Box>
                    </ActionButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ minHeight: 24 }}>
                  {renderNotesContent(internal_notes)}
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

        {/* ==================== RIGHT CONTENT ==================== */}
        <Grid item xs={12} md={9}>
          <Box className="view-release-content">

            {/* Stats Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2, mb: 2 }}>
              <StatCard title="Revenue" value={`$${parseFloat(revenue || 0).toFixed(3)}`} icon={DollarSign} color="#10B981" />
              {/* Consolidated Releases Card */}
              <Box
                sx={{
                  backgroundColor: '#232636',
                  border: '1px solid #2E3247',
                  borderLeft: '4px solid #6C63FF',
                  borderRadius: 2,
                  p: 1.8,
                  gridColumn: 'span 2',
                  minWidth: 140,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#94A3B8', mb: 0.5, fontSize: '0.8rem' }}>
                      Releases
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#F1F5F9', fontWeight: 700, fontSize: '1.4rem' }}>
                      {(parseInt(total_releases) || 0) + (parseInt(total_drafts) || 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', height: 36, backgroundColor: '#2E3247' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Approved', count: total_approved, color: '#10B981' },
                    { label: 'In Review', count: total_in_review, color: '#F59E0B' },
                    { label: 'Rejected', count: total_rejected, color: '#EF4444' },
                    { label: 'Taken Down', count: total_takendown, color: '#EF4444' },
                    { label: 'Drafts', count: total_drafts, color: '#F59E0B' },
                  ].map((s) => (
                    <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        {s.label}: <span style={{ color: '#F1F5F9', fontWeight: 600 }}>{s.count || 0}</span>
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ opacity: 0.3, flexShrink: 0 }}>
                  <Disc3 size={40} color="#6C63FF" />
                </Box>
              </Box>
              <StatCard title="Artists" value={total_artist || 0} icon={Users} color="#3B82F6" />
              <StatCard title="Open Tickets" value={total_open_tickets || 0} icon={Ticket} color="#EF4444" />
            </Box>

            {/* Section: User Information */}
            <Paper className="section-card">
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" className="section-card__title" sx={{ mb: '0 !important' }}>User Information</Typography>
                <ActionButton variant="danger" onClick={() => setEditUserModal(true)}>Edit</ActionButton>
              </Box>
              <Divider sx={{ mb: 2, borderColor: 'var(--border)' }} />
              <CopyableField label="Name" value={name} />
              <CopyableField label="Email" value={email} />
              <CopyableField label="Phone" value={phone} />
              <CopyableField label="Address" value={address} />
              <CopyableField label="Signup Date" value={moment(created_at).format("DD MMM YYYY, hh:mm A")} />
              <CopyableField label="IP Address" value={ip} />
              <CopyableField label="Revoked" value={revoked === 0 ? "No" : "Yes"} highlight={revoked === 1 ? "#EF4444" : undefined} />
              <CopyableField label="Total Releases" value={total_releases} />
              <CopyableField label="Total Drafts" value={total_drafts} />
              <CopyableField label="Total Artists" value={total_artist} />
              <CopyableField label="Reports Requested" value={total_reports_requested} />
              <CopyableField label="Tickets Created" value={total_tickets_created} />
              <CopyableField label="Open Tickets" value={total_open_tickets} highlight={total_open_tickets > 0 ? "#F59E0B" : undefined} />
            </Paper>

            {/* Section: Bank Details */}
            <Paper className="section-card">
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" className="section-card__title" sx={{ mb: '0 !important' }}>Bank Details</Typography>
                {updated_on && (
                  <Chip
                    size="small"
                    icon={<Clock size={12} />}
                    label={`Updated ${getTimeAgo(updated_on)} ago`}
                    sx={{
                      backgroundColor: 'rgba(239, 68, 68, 0.12)',
                      color: '#EF4444',
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      '& .MuiChip-icon': { color: '#EF4444' },
                    }}
                  />
                )}
              </Box>
              <Divider sx={{ mb: 2, borderColor: 'var(--border)' }} />
              <CopyableField label="Bank Name" value={bank_name} />
              <CopyableField label="IFSC Code" value={ifsc_code} />
              <CopyableField label="Account Number" value={account_number} highlight="#F59E0B" />
              <CopyableField label="Holder Name" value={holder_name} />
              <CopyableField label="Branch Name" value={branch_name} />
              <CopyableField label="PAN Number" value={pan} highlight="#EF4444" />
              <CopyableField label="Other Details" value={other_details} />
              <CopyableField label="Updated On" value={formatDate(updated_on)} />
            </Paper>

          </Box>
        </Grid>
      </Grid>

      {/* Add Revenue Modal */}
      <CustomAddModal
        open={openRevenue}
        title="Add/Update Revenue"
        handleClose={() => setOpenRevenue(false)}
        onSubmitConfirm={onUpdateRevenue}
        saveLabel="Update"
      >
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1.5, fontSize: '0.85rem' }}>
            Current revenue: <span style={{ color: '#10B981', fontWeight: 600 }}>${parseFloat(revenue || 0).toFixed(3)}</span>
          </Typography>
          <TextField
            type="number"
            variant="outlined"
            fullWidth
            value={addRevenue}
            onChange={(e) => setAddRevenue(e.target.value)}
            placeholder="Enter revenue amount"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DollarSign size={16} color="#94A3B8" />
                </InputAdornment>
              ),
            }}
            sx={addRevenue !== '' && parseFloat(addRevenue) < parseFloat(revenue || 0) ? {
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#EF4444' },
                '&:hover fieldset': { borderColor: '#EF4444' },
                '&.Mui-focused fieldset': { borderColor: '#EF4444' },
              },
            } : {}}
          />
          {addRevenue !== '' && parseFloat(addRevenue) < parseFloat(revenue || 0) && (
            <Typography variant="body2" sx={{ color: '#EF4444', mt: 0.75, fontSize: '0.8rem' }}>
              New value is less than current revenue (${parseFloat(revenue || 0).toFixed(3)})
            </Typography>
          )}
        </Box>
      </CustomAddModal>

      {/* Revenue Logs Modal */}
      <CustomShowModal
        open={openRevenueLogs}
        handleClose={() => setOpenRevenueLogs(false)}
        title="Revenue Logs"
        maxWidth="sm"
      >
        <Box>
          {(() => {
            let logs = [];
            try {
              logs = userDetails.revenue_logs ? JSON.parse(userDetails.revenue_logs) : [];
            } catch (e) {
              logs = [];
            }
            if (logs.length === 0) {
              return (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textAlign: 'center', py: 3 }}>
                  No logs yet
                </Typography>
              );
            }
            return [...logs].reverse().map((log, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.5,
                  p: 1.5, mb: 1, borderRadius: '8px',
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                }}
              >
                <Activity size={16} style={{ marginTop: 3, flexShrink: 0, color: '#6C63FF' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.85rem' }}>
                    {log.by || 'Admin'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.8rem', mt: 0.25 }}>
                    <span style={{ color: '#EF4444' }}>${parseFloat(log.old || 0).toFixed(3)}</span>
                    {' → '}
                    <span style={{ color: '#10B981' }}>${parseFloat(log.new || 0).toFixed(3)}</span>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>
                    {moment(log.at).format('DD MMM YYYY, hh:mm A')}
                  </Typography>
                </Box>
              </Box>
            ));
          })()}
        </Box>
      </CustomShowModal>

      {/* Edit User Details Modal */}
      <CustomAddModal
        open={editUserModal}
        title="Edit User Details"
        handleClose={() => setEditUserModal(false)}
        onSubmitConfirm={onUpdateUserDetails}
        saveLabel="Save"
      >
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="Name"
            variant="outlined"
            size="small"
            fullWidth
            value={editUserData.name}
            onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
            placeholder="Enter name"
          />
          <TextField
            label="Phone"
            variant="outlined"
            size="small"
            fullWidth
            value={editUserData.phone}
            onChange={(e) => setEditUserData({ ...editUserData, phone: e.target.value })}
            placeholder="Enter phone number"
          />
          <TextField
            label="Address"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={editUserData.address}
            onChange={(e) => setEditUserData({ ...editUserData, address: e.target.value })}
            placeholder="Enter address"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
              Analytics Access
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: editUserData.analytics === "true" ? '#22C55E' : '#F59E0B', fontWeight: 600, fontSize: '0.8rem' }}>
                {editUserData.analytics === "true" ? 'Enabled' : 'Disabled'}
              </Typography>
              <Switch
                checked={editUserData.analytics === "true"}
                onChange={(e) => setEditUserData({ ...editUserData, analytics: e.target.checked ? "true" : "false" })}
                sx={{
                  width: 40,
                  height: 22,
                  p: 0,
                  '& .MuiSwitch-switchBase': {
                    p: 0,
                    m: '3px',
                    transitionDuration: '250ms',
                    '&.Mui-checked': {
                      transform: 'translateX(18px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#22C55E',
                        opacity: 1,
                        border: 0,
                      },
                      '& .MuiSwitch-thumb': {
                        backgroundColor: '#fff',
                        boxShadow: '0 0 6px rgba(34, 197, 94, 0.4)',
                      },
                    },
                    '&.Mui-focusVisible .MuiSwitch-thumb': {
                      boxShadow: '0 0 0 4px rgba(108, 99, 255, 0.2)',
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    width: 16,
                    height: 16,
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    transition: 'all 0.25s ease',
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 11,
                    backgroundColor: '#F59E0B',
                    opacity: 1,
                    transition: 'background-color 0.25s ease',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </CustomAddModal>
    </>
  );
}

export default ViewUserDetails;
